import { FC, useEffect, useState } from "react";
import { IGetShuttleInfo, IStudent } from "../../../models/students.model";
import { Table, TableColumnsType } from 'antd';
import Button from '../../Button';
import SelectShuttleModal from "./modals/SelectShuttleModal";
import useAxios from "../../../hooks/useAxios";
import { createNotRideStudent, getNotRideStudent, getShuttleInfo } from "../../../api/students.api";
import Loader from "../../Loader";
import ErrorResponse from "../../ErrorResponse";
import { getCommonCodeLabel } from "../../../lib/store";
import { deleteStudent } from "../../../api/shuttleSetting.api";
import useNotification from "../../../hooks/useNotification";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../modals/ConfirmModal";
import styled from "styled-components";

const TableEmptyBlock = styled.div`
    font-size: 14px;
    color: #666666;
    padding: 50px 0;
    background-color: #fbfbfc;
    text-align: center;
`

interface IProps {
  student: IStudent | undefined;
}

const ShuttleInfo: FC<IProps> = ({ student }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();  
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetShuttleInfo>();  
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();
  const { data: getNotRideRes, isLoading : isLoading2, error: error2, execute: getNotRideExecute } = useAxios<any>();
  const { data: createNotRideRes, isLoading : isLoading3, error: error3, execute: createNotRideExecute } = useAxios<string>();
  const [selectShuttleVisible, setSelectShuttleVisible] = useState(false);
  const [confirmNotRideVisible, setConfirmNotRideVisible] = useState(false);
  const [isNotRide, setIsNotRide] = useState(false);
  const [hasShuttle, setHasShuttle] = useState(false);

  useEffect(() => {
    if (student) doGetShuttleInfo();
  }, [student]);

  const doGetShuttleInfo = () => {
    if (!student) return;
    execute(getShuttleInfo(student.campusCode, student.studentCode));
    getNotRideExecute(getNotRideStudent(student.studentCode));
  }

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      doGetShuttleInfo();
      infoNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  useEffect(() => {
    if (getNotRideRes && !isLoading2) {
      if(getNotRideRes?.data && getNotRideRes?.data === 'Y') {
        setIsNotRide(true);
      } else {
        setIsNotRide(false);
      }
    }
  }, [getNotRideRes, isLoading2]);

  useEffect(() => {
    if (createNotRideRes && !isLoading3) {      
      successNotification(t('common.button.processed'));
      doGetShuttleInfo();
      setConfirmNotRideVisible(false);
    }
  }, [createNotRideRes, isLoading3]);

  useEffect(() => {
    if(res) {
      if(res.data?.length > 0) {
        setHasShuttle(true)
      }
    }

  }, [res, isLoading])

  const onDeleteShuttle = (rideStudentCode: number) => {
    if (!student) return;
    deleteExecute(deleteStudent(student.campusCode, rideStudentCode));
  }

  const onNotRideStudent = () => {
    if (!student) return;
    createNotRideExecute(createNotRideStudent(student.studentCode));
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('common.column.category'),
      dataIndex: 'rideGbn',
      align: 'center',
      render: (value) => value === 'UP' ? t('student.student.column67')
      : t('student.student.column68'),
    },
    {
      title: t('student.student.column18'),
      dataIndex: 'weekdayDiv',
      align: 'center',
      render: (value) => getCommonCodeLabel('SY0001', value, 'ko')
    },
    {
      title: t('common.column.time'),
      dataIndex: 'lineTime',
      align: 'center',
    },
    {
      title: t('student.student.column19'),
      dataIndex: 'busName',
      align: 'center',
    },
    {
      title: t('student.student.column20'),
      dataIndex: 'stationName',
      align: 'center',
    },
    {
      title: t('common.button.delete'),
      dataIndex: 'rideStudentCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small gray"
            onClick={() => onDeleteShuttle(value)}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      )
    },
  ];

  return (
    <>
      {isLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />

      <div className="comm-bx__space mt-5">
        <h3 className='sub-title'>{t('student.student.text4')}</h3>
      </div>
      {isNotRide ?
        <div className="comm-table__area mt-0">
          <TableEmptyBlock className='comm-nodata-area mt-0'>
            {t('student.shuttle.message3')}
          </TableEmptyBlock>
        </div>
        :
        <Table
          className='comm-table__area'
          columns={columns}
          dataSource={res?.data}
          pagination={{
            pageSize: 99999
          }}
          rowKey="runningStationCode"
        />
      }
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={() => setConfirmNotRideVisible(true)}
            text={t('student.shuttle.button1')}
            writeAuthYn={writeAuthYn}
            disabled={isNotRide?true:false}
          />
          <Button
            className="btn-basic pink-line"
            onClick={() => setSelectShuttleVisible(true)}
            text={t('student.student.button4')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>

      {selectShuttleVisible &&
        <SelectShuttleModal
          visible={selectShuttleVisible}
          setVisible={setSelectShuttleVisible}
          campusCode={student?.campusCode}
          studentCode={student?.studentCode}
          doGetShuttleInfo={doGetShuttleInfo}
        />      
      }

      {confirmNotRideVisible && (
        <ConfirmModal
          visible={confirmNotRideVisible}
          setVisible={setConfirmNotRideVisible}
          onConfirm={onNotRideStudent}
          titleText={t('student.shuttle.title1')}
          messageText={hasShuttle ? t('student.shuttle.message1') + '\n' + t('student.shuttle.message2') : t('student.shuttle.message2') }
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
          />
        )}
    </>
  );
}

export default ShuttleInfo;
